/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect} from "react"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {useHistory} from "react-router-dom"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {useModel} from "hooks"
import {
    formatCurrency,
    hasActiveRecurringPaymentForPlan,
    isPaymentPlanItemUpNextForRecurring,
    toDateOnly
} from "helpers"
import {KlassappTableProps, PaginationPerPage} from "types/common"
import styles from "./PaymentPlanItemsTable.module.css"
import {StudentCharge} from "types/student-account/student-ledger"
import {preventMinusCharacter} from "helpers/inputHelper"
import NumberFormat from "react-number-format"

import {PaymentPlanItem} from "types/student-account/payment-plan"
import {StudentRecurringPayment} from "types/student-account/student-payment"
import LedgerAccountSelect from "components/LedgerAccountSelect"
import {LedgerAccount, LedgerAccountType} from "types/student-account/ledger-accounts"
import {orderBy, sumBy} from "lodash"
import {useQuery} from "@tanstack/react-query"
import {generalSettingsService} from "services"
import {GeneralSettingField} from "types/settings/general-setting"
import classNames from "classnames"

interface Props extends KlassappTableProps {
    studentPaymentAmount?: number
    paymentPlanItems?: PaymentPlanItem[]
    selectedPaymentItems?: PaymentPlanItem[]
    setSelectedPaymentItems?: React.Dispatch<React.SetStateAction<PaymentPlanItem[]>>
    isAccountStatementReport?: boolean
    recurringPayments?: StudentRecurringPayment[]
}

const PaymentPlanItemsTable: React.FC<Props> = ({
    dispatch,
    dispatchFunc,
    page,
    total,
    pageSize,
    columns,
    data,
    allFields,
    fields,
    tableHeaderActions,
    isLoading,
    menuActions,
    isHideMenuActions,
    isShowTableHeaderAction,
    isLoadedTableFuncs,
    getCurrentData,
    getCurrentPage,
    getCurrentPageSize,
    onChangeFields,
    onDraggableColumn,
    onChangeAllFields,
    onUpdateTableData,
    onChangePage,
    onChangeRowPerPage,
    studentPaymentAmount,
    paymentPlanItems,
    selectedPaymentItems,
    setSelectedPaymentItems,
    isAccountStatementReport = false,
    recurringPayments,
    className
}) => {
    const history = useHistory()
    const model = useModel()
    const userDateFormat = model.getUserDateFormat()
    const {t} = useTranslation(["studentAccount", "common"])

    const settingsQuery = useQuery(
        ["sa-allow-partial-payment"],
        async () => {
            return await generalSettingsService.getSettings([GeneralSettingField.SaAllowPartialPayments])
        },
        {
            initialData: []
        }
    )

    const allowPartialPayments = !!JSON.parse(
        settingsQuery.data?.find((setting) => setting.field === GeneralSettingField.SaAllowPartialPayments)?.value ??
            null
    )

    const getData = React.useCallback(async () => {
        try {
            dispatch({isLoading: true})
            const page = getCurrentPage()
            const pageSize = getCurrentPageSize()
            const filteredPaymentPlanItems = orderBy(
                paymentPlanItems.filter(
                    (item) =>
                        item.paidAmount < item.amount ||
                        selectedPaymentItems?.some(
                            ({paymentPlanItemId}) => paymentPlanItemId === item.paymentPlanItemId
                        )
                ),
                ["dueDate"],
                ["asc"]
            )
            dispatch({
                total: filteredPaymentPlanItems.length,
                data: filteredPaymentPlanItems.slice((page - 1) * pageSize, page * pageSize).map((item) => {
                    const selectedItem = selectedPaymentItems?.find(
                        ({paymentPlanItemId}) => paymentPlanItemId === item.paymentPlanItemId
                    )
                    return {
                        ...item,
                        id: item.paymentPlanItemId,
                        isChecked: !!selectedItem,
                        isActiveTableCol: !!selectedItem,
                        paymentAmount: selectedItem?.paymentAmount ?? item.paymentAmount,
                        paymentAccountId: selectedItem?.paymentAccountId ?? item.paymentAccountId
                    }
                })
            })
        } catch (error) {
            console.error(error)
        } finally {
            dispatch({isLoading: false})
        }
    }, [paymentPlanItems, selectedPaymentItems])

    const onClickRowItem = React.useCallback((item: StudentCharge) => {}, [])

    const getFields = React.useCallback(() => {
        return [
            t("payments.column.id"),
            t("payments.column.paymentPlanId"),
            t("payments.column.originalAmount"),
            t("payments.column.paidAmount"),
            t("payments.column.dueDate"),
            t("payments.column.status"),
            t("payments.column.payment"),
            t("payments.column.transactionType")
        ]
    }, [])

    const getColumns = React.useCallback(
        () =>
            [
                {
                    title: t("payments.column.id"),
                    field: "paymentPlanItemId"
                },
                isAccountStatementReport
                    ? false
                    : {
                          title: t("payments.column.paymentPlanId"),
                          field: "paymentPlanCode"
                      },
                {
                    title: t("payments.column.originalAmount"),
                    field: "amount",
                    render: (value) => formatCurrency(value),
                    renderText: (value: number) => (value ?? 0).toFixed(2)
                },
                isAccountStatementReport
                    ? false
                    : {
                          title: t("payments.column.paidAmount"),
                          field: "paidAmount",
                          render: (value) => formatCurrency(value),
                          renderText: (value: number) => (value ?? 0).toFixed(2)
                      },
                {
                    title: t("payments.column.dueDate"),
                    field: "dueDate",
                    render: (_, record: PaymentPlanItem) => {
                        const dueDate = moment(toDateOnly(record.dueDate))
                        if (!record.paidAmount) {
                            const numDays = Math.abs(moment().diff(dueDate, "day"))
                            const isPastDue = dueDate.isBefore(Date.now(), "day")
                            const isDue = moment().add(20, "day").isAfter(dueDate)
                            if (isPastDue)
                                return (
                                    <span className={styles.pastDue}>
                                        {dueDate.format(userDateFormat)}
                                        <br />
                                        Past Due{" "}
                                        {t(!numDays ? "paymentPlan.today" : "paymentPlan.numDays", {
                                            count: numDays
                                        })}
                                    </span>
                                )
                            if (isDue)
                                return (
                                    <span className={styles.due}>
                                        {dueDate.format(userDateFormat)}
                                        <br />
                                        Due{" "}
                                        {t(!numDays ? "paymentPlan.today" : "paymentPlan.numDays", {
                                            count: numDays
                                        })}
                                    </span>
                                )
                        }
                        return dueDate.format(userDateFormat)
                    }
                },
                isAccountStatementReport
                    ? false
                    : {
                          title: t("payments.column.status"),
                          field: "status",
                          render: (_, record: PaymentPlanItem) => {
                              const isFullyPaid = record.paidAmount >= record.amount
                              const isUpNext =
                                  !isFullyPaid &&
                                  isPaymentPlanItemUpNextForRecurring(recurringPayments ?? [], record.paymentPlanItemId)
                              const hasActiveRecurring =
                                  !isFullyPaid &&
                                  !isUpNext &&
                                  hasActiveRecurringPaymentForPlan(recurringPayments ?? [], record.paymentPlanId)
                              return (
                                  <>
                                      {isFullyPaid && (
                                          <span className={classNames(styles.status, "completed")}>Completed</span>
                                      )}
                                      {isUpNext && (
                                          <span className={classNames(styles.status, "in_progress")}>In Progress</span>
                                      )}
                                      {hasActiveRecurring && (
                                          <span className={styles.activeRecurringMessage}>
                                              You have an active recurring payment
                                          </span>
                                      )}
                                  </>
                              )
                          }
                      },
                isAccountStatementReport
                    ? false
                    : {
                          title: t("payments.column.payment"),
                          field: "paymentAmount",
                          render: (_, record: PaymentPlanItem & {isChecked?: boolean}) =>
                              !record.isChecked ? null : (
                                  <NumberFormat
                                      prefix="$"
                                      thousandSeparator
                                      decimalScale={2}
                                      fixedDecimalScale
                                      className={styles.input}
                                      onKeyDown={preventMinusCharacter}
                                      min={1}
                                      placeholder={t(t("payments.column.amount"))}
                                      readOnly={!allowPartialPayments}
                                      value={record.paymentAmount}
                                      onValueChange={({floatValue}) => {
                                          dispatch({
                                              data: getCurrentData().map((item: PaymentPlanItem) =>
                                                  item.paymentPlanItemId === record.paymentPlanItemId
                                                      ? {...item, paymentAmount: floatValue}
                                                      : item
                                              )
                                          })
                                          setSelectedPaymentItems?.((prev) =>
                                              prev.map((item) =>
                                                  item.paymentPlanItemId !== record.paymentPlanItemId
                                                      ? item
                                                      : {...item, paymentAmount: floatValue}
                                              )
                                          )
                                      }}
                                  />
                              )
                      },
                isAccountStatementReport
                    ? false
                    : model.isStaffOrAdmin() && {
                          title: t("payments.column.transactionType"),
                          field: "paymentAccountId",
                          render: (_, record: PaymentPlanItem & {isChecked?: boolean}) =>
                              !record.isChecked ? null : (
                                  <LedgerAccountSelect
                                      placeholder={t("payments.column.transactionType")}
                                      active
                                      isClearable
                                      isMulti={false}
                                      value={record.paymentAccountId}
                                      onChange={(account?: LedgerAccount) => {
                                          dispatch({
                                              data: getCurrentData().map((item: PaymentPlanItem) =>
                                                  item.paymentPlanItemId === record.paymentPlanItemId
                                                      ? {...item, paymentAccountId: account?.accountId ?? null}
                                                      : item
                                              )
                                          })
                                          setSelectedPaymentItems?.((prev) =>
                                              prev.map((item) =>
                                                  item.paymentPlanItemId !== record.paymentPlanItemId
                                                      ? item
                                                      : {...item, paymentAccountId: account?.accountId ?? null}
                                              )
                                          )
                                      }}
                                      components={{
                                          ClearIndicator: () => <div className="w-20" />,
                                          DropdownIndicator: () => null,
                                          IndicatorSeparator: () => null
                                      }}
                                      menuPortalTarget={document.body}
                                      stylesCustom={{
                                          menuPortal: (styles) => ({
                                              ...styles,
                                              zIndex: 9999
                                          }),
                                          menu: (styles) => ({
                                              ...styles,
                                              width: "max-content",
                                              minWidth: "100%",
                                              maxWidth: "450px",
                                              right: 0
                                          })
                                      }}
                                  />
                              )
                      }
            ].filter(Boolean),
        [t, userDateFormat, allowPartialPayments, isAccountStatementReport, recurringPayments]
    )

    const getTableHeaderActions = React.useCallback(
        (isShowDuplicateBtn = true, checkedData = []) => {
            return []
        },
        [t]
    )

    const getMenuActions = React.useCallback(() => {
        return []
    }, [t])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: () => ""},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
        setTimeout(() => {
            onChangeRowPerPage(PaginationPerPage.item10)
        }, 500)
    }, [])

    useEffect(() => {
        dispatchFunc([
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [getColumns, getFields])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, paymentPlanItems])

    const handleUpdateTableData = React.useCallback(
        (updatedData: Array<PaymentPlanItem & {isChecked: boolean}>) => {
            dispatch({data: updatedData})
            setSelectedPaymentItems?.((prev = []) => {
                const updatedItemIds = new Set(updatedData.map((item) => item.paymentPlanItemId))
                const preservedItems = prev.filter((item) => !updatedItemIds.has(item.paymentPlanItemId))

                return [...preservedItems, ...updatedData.filter((item) => item.isChecked)]
            })
        },
        [setSelectedPaymentItems]
    )

    const handleUpdateRowData = React.useCallback(
        (updatedRow: PaymentPlanItem & {isChecked: boolean}) => {
            const otherCheckedItems =
                selectedPaymentItems?.filter((item) => item.paymentPlanItemId !== updatedRow.paymentPlanItemId) ?? []
            const newData = getCurrentData().map((item: PaymentPlanItem) => {
                if (item.paymentPlanItemId !== updatedRow.paymentPlanItemId) {
                    return item
                }
                let paymentAmount: number
                let paymentAccountId: number
                if (!updatedRow.isChecked) {
                    paymentAmount = undefined
                    paymentAccountId = undefined
                } else {
                    const paymentAmountDue = Math.max(
                        0,
                        studentPaymentAmount - sumBy(otherCheckedItems, "paymentAmount")
                    )
                    const itemAmountDue = Math.max(0, updatedRow.amount - updatedRow.paidAmount)
                    // paymentAmount = Math.min(itemAmountDue, paymentAmountDue)
                    paymentAmount = itemAmountDue
                    paymentAccountId = updatedRow.paymentAccountId ?? updatedRow.paymentPlan?.accountId
                }
                return {
                    ...updatedRow,
                    isActiveTableCol: !!updatedRow.isChecked,
                    paymentAmount,
                    paymentAccountId
                }
            })
            dispatch({data: newData})
            setSelectedPaymentItems?.(
                !updatedRow.isChecked
                    ? otherCheckedItems
                    : [
                          ...otherCheckedItems,
                          newData.find((item) => item.paymentPlanItemId === updatedRow.paymentPlanItemId)
                      ]
            )
        },
        [studentPaymentAmount, selectedPaymentItems]
    )

    return (
        <div className={classNames(styles.wrap, className)}>
            {!isAccountStatementReport && (
                <KlassappTableHeader
                    isShowAction={isShowTableHeaderAction}
                    actions={tableHeaderActions}
                    page={page}
                    total={total}
                    defaultPageSize={pageSize}
                    onChangePage={onChangePage}
                    onChangeRowPerPage={onChangeRowPerPage}
                />
            )}
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                menuActions={isHideMenuActions ? [] : menuActions}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn={!isAccountStatementReport}
                onClickRowItem={onClickRowItem}
                onChangeFields={onChangeFields}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
                onUpdateRowData={handleUpdateRowData}
                onUpdateTableData={handleUpdateTableData}
            />
        </div>
    )
}

export default KlassappTableHOC(PaymentPlanItemsTable)
